import { useEffect, useRef } from "react";

const HEALTHY_MS = 3_000;
const MAX_RETRY_MS = 30_000;

export function createBoundedPoll(
  read: () => Promise<boolean>,
  random: () => number = Math.random,
) {
  let active = false;
  let disposed = false;
  let running = false;
  let restart = false;
  let failures = 0;
  let nextAllowed = 0;
  let timer: number | null = null;
  const clear = () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };
  const schedule = (delay: number) => {
    clear();
    if (active && !disposed) timer = window.setTimeout(() => void run(), delay);
  };
  const run = async () => {
    timer = null;
    if (!active || disposed || running) return;
    running = true;
    let success = false;
    try {
      success = await read();
    } catch {
      success = false;
    } finally {
      running = false;
      failures = success ? 0 : Math.min(failures + 1, 4);
      const base = success
        ? HEALTHY_MS
        : Math.min(MAX_RETRY_MS, HEALTHY_MS * 2 ** failures);
      const delay =
        base === MAX_RETRY_MS
          ? 24_000 + Math.floor(random() * 6_000)
          : base + Math.floor(random() * (success ? 1_000 : base / 4));
      nextAllowed = Date.now() + delay;
      if (disposed || !active) return;
      if (restart) {
        restart = false;
        if (success) void run();
        else schedule(Math.max(0, nextAllowed - Date.now()));
      } else {
        schedule(Math.max(0, nextAllowed - Date.now()));
      }
    }
  };
  return {
    recover() {
      if (!active || disposed || failures) return;
      if (running) return;
      clear();
      void run();
    },
    setActive(next: boolean) {
      if (disposed || next === active) return;
      active = next;
      clear();
      if (next) {
        if (running) restart = true;
        else if (failures) schedule(Math.max(0, nextAllowed - Date.now()));
        else void run();
      } else restart = false;
    },
    dispose() {
      disposed = true;
      active = false;
      clear();
    },
  };
}

export function useBoundedPoll(
  active: boolean,
  read: () => Promise<boolean>,
  sessionKey = 0,
) {
  const readRef = useRef(read);
  readRef.current = read;
  const pollRef = useRef<ReturnType<typeof createBoundedPoll> | null>(null);
  useEffect(() => {
    const poll = createBoundedPoll(() => readRef.current());
    pollRef.current = poll;
    const recover = () => poll.recover();
    window.addEventListener("focus", recover);
    return () => {
      window.removeEventListener("focus", recover);
      poll.dispose();
      pollRef.current = null;
    };
  }, [sessionKey]);
  useEffect(() => {
    pollRef.current?.setActive(active);
  }, [active, sessionKey]);
}
